/**
 * Pure helpers for Discover Requirements tab formatting (AB#10970).
 * Preserves intentional paragraph breaks and restores hierarchy for
 * roman / letter markers, bullets, circled digits, and embedded headings.
 */

export type RequirementSegmentKind =
  | "intro"
  | "heading"
  | "roman"
  | "letter"
  | "bullet"
  | "circled"
  | "paragraph";

export interface RequirementSegment {
  kind: RequirementSegmentKind;
  /** Marker label without parens, e.g. "i", "A", "①" */
  label?: string;
  text: string;
}

const ROMAN_BODY = "i{1,3}|iv|vi{0,3}|ix|xi{1,3}|xii";
const ROMAN_RE = new RegExp(`\\((?:${ROMAN_BODY})\\)`, "i");
const ROMAN_SPLIT = new RegExp(`(?=\\((?:${ROMAN_BODY})\\))`, "i");
const ROMAN_CAPTURE = new RegExp(`^\\((${ROMAN_BODY})\\)\\s*(.*)`, "is");

const LETTER_RE = /\([A-Z]\)/;
const LETTER_SPLIT = /(?=\([A-Z]\))/;
const LETTER_CAPTURE = /^\(([A-Z])\)\s*(.*)/s;

/** CMS-style section headings that should stand out when embedded. */
const HEADING_PATTERNS: RegExp[] = [
  /\bDEFINITIONS\b/gi,
  /\bSurvey Procedures\b[^\n.]{0,40}/gi,
  /\bNOTE:\s*/gi,
  /\bExample of a Well-Designed[^\n.]{0,80}/gi,
  /\bKEY ELEMENTS OF NON-COMPLIANCE\b/gi,
  /\bDEFICIENCY CATEGORIZATION\b/gi,
  /\bINVESTIGATIVE PROCEDURES?\b/gi,
  /\bINTENT\b/gi,
  /\bGUIDANCE\b/gi,
];

const CIRCLED_SPLIT = /(?=[\u2460-\u2473])/u;
const BULLET_CHAR = "•";

/**
 * Normalize whitespace carefully: keep newlines that structure the text,
 * collapse runs of horizontal space, and cap blank lines at one.
 */
export function normalizeRequirementWhitespace(text: string): string {
  if (!text) return "";
  return text
    .replace(/\r\n/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Insert newlines before structural markers so dense single-line API blobs
 * regain readable hierarchy without destroying existing breaks.
 */
export function insertRequirementStructuralBreaks(text: string): string {
  let t = normalizeRequirementWhitespace(text);
  if (!t) return "";

  // Circled digits ①–⑳ as major section breaks
  t = t
    .split(CIRCLED_SPLIT)
    .map((p) => p.trim())
    .filter(Boolean)
    .join("\n\n");

  // Bullets
  t = t.replace(/\s*•\s*/g, `\n${BULLET_CHAR} `);

  // Roman markers (i)/(ii)/… — prefer break after sentence punctuation or space
  t = t.replace(
    new RegExp(`([.;:])\\s*(\\((?:${ROMAN_BODY})\\))`, "gi"),
    "$1\n\n$2",
  );
  t = t.replace(
    new RegExp(`([^\\n])\\s+(\\((?:${ROMAN_BODY})\\))`, "gi"),
    "$1\n\n$2",
  );

  // Letter markers (A)/(B)/…
  t = t.replace(/([.;:\n])\s*(\([A-Z]\))/g, "$1\n$2");
  t = t.replace(/([^\n])\s+(\([A-Z]\))/g, "$1\n$2");

  // Embedded headings — put each on its own line when jammed mid-prose
  for (const re of HEADING_PATTERNS) {
    t = t.replace(re, (match, offset: number, whole: string) => {
      const prev = offset > 0 ? whole[offset - 1] : "\n";
      const prefix = prev === "\n" ? "" : "\n\n";
      return `${prefix}${match.trim()}`;
    });
  }

  return normalizeRequirementWhitespace(t);
}

function isHeadingLine(line: string): boolean {
  const u = line.trim();
  if (!u) return false;
  if (/^DEFINITIONS\b/i.test(u)) return true;
  if (/^Survey Procedures\b/i.test(u)) return true;
  if (/^NOTE:\s*/i.test(u)) return true;
  if (/^Example of a Well-Designed/i.test(u)) return true;
  if (/^KEY ELEMENTS OF NON-COMPLIANCE\b/i.test(u)) return true;
  if (/^DEFICIENCY CATEGORIZATION\b/i.test(u)) return true;
  if (/^INVESTIGATIVE PROCEDURES?\b/i.test(u)) return true;
  if (/^(INTENT|GUIDANCE)\b/i.test(u) && u.length < 80) return true;
  return false;
}

function parseLineCluster(cluster: string): RequirementSegment[] {
  const trimmed = cluster.trim();
  if (!trimmed) return [];

  if (isHeadingLine(trimmed)) {
    return [{ kind: "heading", text: trimmed.replace(/^NOTE:\s*/i, "NOTE: ").trim() }];
  }

  const circled = trimmed.match(/^([\u2460-\u2473])\s*(.*)$/su);
  if (circled) {
    return [{ kind: "circled", label: circled[1], text: circled[2].trim() }];
  }

  if (trimmed.startsWith(BULLET_CHAR)) {
    return [{ kind: "bullet", text: trimmed.replace(/^•\s*/, "").trim() }];
  }

  const romanMatch = trimmed.match(ROMAN_CAPTURE);
  if (romanMatch) {
    const label = romanMatch[1].toLowerCase();
    const rest = romanMatch[2].trim();
    if (!LETTER_RE.test(rest)) {
      return [{ kind: "roman", label, text: rest.replace(/;\s*$/, "") }];
    }
    const subChunks = rest.split(LETTER_SPLIT);
    const firstIsLetter = LETTER_RE.test(subChunks[0].slice(0, 4));
    const preamble = firstIsLetter
      ? ""
      : subChunks[0].trim().replace(/:\s*$/, ":");
    const letterChunks = firstIsLetter ? subChunks : subChunks.slice(1);
    const out: RequirementSegment[] = [];
    if (preamble) {
      out.push({ kind: "roman", label, text: preamble });
    } else {
      out.push({ kind: "roman", label, text: "" });
    }
    for (const sub of letterChunks) {
      const sm = sub.match(LETTER_CAPTURE);
      if (!sm) continue;
      out.push({
        kind: "letter",
        label: sm[1],
        text: sm[2].trim().replace(/;\s*$/, ""),
      });
    }
    return out;
  }

  const letterOnly = trimmed.match(LETTER_CAPTURE);
  if (letterOnly) {
    return [
      {
        kind: "letter",
        label: letterOnly[1],
        text: letterOnly[2].trim().replace(/;\s*$/, ""),
      },
    ];
  }

  return [{ kind: "paragraph", text: trimmed }];
}

/**
 * Parse requirement prose into ordered display segments.
 */
export function parseRequirementText(text: string): RequirementSegment[] {
  if (!text?.trim()) return [];

  const prepared = insertRequirementStructuralBreaks(text);
  if (!prepared) return [];

  // Prefer blank-line paragraphs; fall back to single newlines for marker lines
  const roughBlocks = prepared.includes("\n\n")
    ? prepared.split(/\n{2,}/)
    : prepared.split(/\n/);

  const segments: RequirementSegment[] = [];
  for (const block of roughBlocks) {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) continue;

    // If a block still mixes markers on one line, re-split by markers
    for (const line of lines) {
      if (
        ROMAN_RE.test(line) ||
        LETTER_RE.test(line) ||
        line.includes(BULLET_CHAR) ||
        /[\u2460-\u2473]/u.test(line)
      ) {
        // Further split dense line by roman / bullet / circled
        let pieces = [line];
        if (ROMAN_RE.test(line)) {
          pieces = line.split(ROMAN_SPLIT).map((p) => p.trim()).filter(Boolean);
        }
        const expanded: string[] = [];
        for (const piece of pieces) {
          if (piece.includes(BULLET_CHAR) || /[\u2460-\u2473]/u.test(piece)) {
            const byBullet = piece
              .replace(/\s*•\s*/g, `\n${BULLET_CHAR} `)
              .split(CIRCLED_SPLIT)
              .flatMap((p) => p.split("\n"))
              .map((p) => p.trim())
              .filter(Boolean);
            expanded.push(...byBullet);
          } else {
            expanded.push(piece);
          }
        }
        for (const piece of expanded) {
          segments.push(...parseLineCluster(piece));
        }
      } else {
        segments.push(...parseLineCluster(line));
      }
    }
  }

  // Merge empty roman preamble with following letter-only is already handled.
  // Drop completely empty paragraph segments.
  return segments.filter((s) => s.kind === "roman" || s.kind === "heading" || Boolean(s.text));
}
